"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function NationalHeadquarters() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224203" id="our-location" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-secondary)_0%,transparent_15%)] opacity-10 pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div className={`transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative group">
              {}
              <div className="absolute -top-4 -left-4 w-8 h-8 border-t border-l border-primary/30 transition-all duration-500 group-hover:border-secondary group-hover:-translate-x-1 group-hover:-translate-y-1" />
              <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b border-r border-primary/30 transition-all duration-500 group-hover:border-secondary group-hover:translate-x-1 group-hover:translate-y-1" />
              
              <div className="border border-border bg-card shadow-xl rounded-lg p-2 backdrop-blur-sm">
                <AspectRatio ratio={4 / 3} className="bg-muted overflow-hidden rounded-md">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/atlanta_logistics_hub_map.png" alt="Map showing the GOTMEDS.IO Atlanta Logistics Hub location" className="object-cover w-full h-full opacity-90 transition-all duration-700 group-hover:scale-105" />
                  {}
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent pointer-events-none" />
                </AspectRatio>
              </div>
            </div>
          </div>

          {}
          <div className={`flex flex-col justify-center transition-all duration-1000 delay-300 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-sm uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-4 font-mono">
              <span className="w-8 h-px bg-secondary" />
              Atlanta Logistics Hub
            </h2>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground mb-16 leading-tight tracking-tight">
              Swift <br className="hidden md:block" />
              <span className="text-primary">Prescription</span> <span className="text-secondary italic font-serif">Delivery</span>
            </h3>

            <div className="space-y-12">
              {}
              <div className="group flex items-start gap-6">
                <div className="mt-2 text-primary group-hover:text-secondary transition-colors duration-300">
                  <MapPin className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-mono">
                    Distribution Center
                  </p>
                  <p className="text-2xl md:text-3xl font-default font-semibold text-foreground leading-snug">
                    Atlanta Metro Area
                  </p>
                </div>
              </div>

              {}
              <div className="group flex items-start gap-6">
                <div className="mt-2 text-primary group-hover:text-secondary transition-colors duration-300">
                  <Phone className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-mono">
                    Patient Support
                  </p>
                  <Link to="tel:(678) 358-0270" className="text-2xl md:text-3xl font-default font-semibold text-primary hover:text-secondary transition-colors duration-300 inline-block">
                    (678) 358-0270
                  </Link>
                </div>
              </div>

              {}
              <div className="group flex items-start gap-6">
                <div className="mt-2 text-primary group-hover:text-secondary transition-colors duration-300">
                  <Mail className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-mono">
                    Secure Inquiries
                  </p>
                  <Link to="mailto:info@gotmeds.io" className="text-2xl md:text-3xl font-default font-semibold text-primary hover:text-secondary transition-colors duration-300 inline-block break-all">
                    info@gotmeds.io
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>;
}